import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, FileText, ImagePlus, Trash2, Upload } from 'lucide-react';
import { Button } from './ui';
import {
  addEventImagePage, addPdfPage, fetchPdfPageFile, fetchPdfPages, movePdfPage, removePdfPage, setPdfPageShown,
  setSponsorsPosition,
} from './ProgramAdminApi';
import { entryPath, isEntry, nodeLabel, nodeTitle } from './programAdminModel';
import { PanelHint } from './programAdminStyles';
import { mediaUrl } from '../../utils/mediaUrl';

// The program's own pages (programimport.ProgramPdfPages): title pages ("cover", e.g. the poster with
// logo, title, date and venue) in front of the program PDF and on the event page in place of its main
// image, and sponsor pages ("sponsors") where the admin places them - by default below the program,
// above the speakers and faculty, on the website and in the PDF alike. Each part is a list of JPEG,
// PNG or PDF files (a PDF with all its pages) in the admin's order, in German and English, also in the
// admin's previews. While there are no title pages and the event page's main image (the old event
// form's "Main Image") is a JPEG or PNG, it is offered as the start. The website shows a change once
// the program is published; the public PDF when it is made again.
//   usePdfPages     the event's pages, read once, for the fields that share them
//   PdfPagesField   one part's list: thumbnails of images, upload, order, remove, whether each file is
//                   shown on the website and in the PDF; for the sponsor pages also where they stand
//   PdfPagesEntries the two entries of the Program area, each opening its field

const PARTS = {
  cover: {
    title: ['programAdmin.pages.cover.title', 'Title pages'],
    intro: ['programAdmin.pages.cover.intro', 'Images (JPEG, PNG) or PDFs - for instance the poster with logo, title, date and venue. They come before the program, in this order, in the German and the English PDF, and the event page shows them in place of its main image; a PDF with all its pages.'],
    none: ['programAdmin.pages.cover.none', 'No title page: the PDF starts with the program.'],
  },
  sponsors: {
    title: ['programAdmin.pages.sponsors.title', 'Sponsor pages'],
    intro: ['programAdmin.pages.sponsors.intro', 'Images (JPEG, PNG) or PDFs with the sponsors and partners. They stand where you choose below, in this order, on the event page and in the German and the English PDF (there each on a page of its own); a PDF with all its pages.'],
    none: ['programAdmin.pages.sponsors.none', 'No sponsor page yet.'],
  },
};

export const usePdfPages = (eventId) => {
  const [view, setView] = useState(null);
  useEffect(() => {
    let live = true;
    fetchPdfPages(eventId).then((v) => { if (live) setView(v); }).catch(() => { if (live) setView(null); });
    return () => { live = false; };
  }, [eventId]);
  return [view, setView];
};

// Object URLs of the image pages' files, read once per page id and revoked when they go.
const useThumbnails = (eventId, pages) => {
  const [urls, setUrls] = useState({});
  const ids = pages.filter((p) => p.kind === 'image').map((p) => p.id).join(',');
  useEffect(() => {
    let live = true;
    const wanted = ids ? ids.split(',').map(Number) : [];
    setUrls((old) => {
      Object.entries(old).forEach(([id, url]) => { if (!wanted.includes(Number(id))) URL.revokeObjectURL(url); });
      return Object.fromEntries(Object.entries(old).filter(([id]) => wanted.includes(Number(id))));
    });
    wanted.forEach((id) => {
      fetchPdfPageFile(eventId, id)
        .then((blob) => { if (live) setUrls((old) => (old[id] ? old : { ...old, [id]: URL.createObjectURL(blob) })); })
        .catch(() => {});
    });
    return () => { live = false; };
  }, [eventId, ids]);
  return urls;
};

// The top-level entries the sponsor pages can follow, in the program's order: day by day, by start
// time, removed ones left out.
const entryChoices = (program, language) => {
  const days = (program?.days || []).map((d) => d.id);
  const dayIndex = (e) => { const i = days.indexOf(e.eventDayId); return i < 0 ? days.length : i; };
  return (program?.entries || [])
    .filter((n) => isEntry(n) && n.removedAt == null)
    .slice()
    .sort((a, b) => dayIndex(a) - dayIndex(b) || String(a.startTime || '99').localeCompare(String(b.startTime || '99')))
    .map((e) => ({
      id: e.id,
      text: [entryPath(program, e, language), nodeTitle(e, language) || nodeLabel(e, language)].filter(Boolean).join(' – ') || `#${e.id}`,
    }));
};

// Where the sponsor pages stand: below the program before the speakers and faculty (the default),
// at the very end, or after an entry the admin picks (its slot on its day; printed, the pages follow
// the page that entry ends on).
const SponsorsPlacement = ({ eventId, view, program, language, busy, run }) => {
  const intl = useIntl();
  const t = (id, def) => intl.formatMessage({ id, defaultMessage: def });
  const choices = entryChoices(program, language);
  const position = view.sponsorsPosition || 'BEFORE_PEOPLE';
  const after = view.sponsorsAfterEntryId;
  const value = position === 'AFTER_ENTRY' ? `entry-${after}` : position;
  const saved = t('programAdmin.pages.positionSaved', 'Saved. The website shows it once the program is published; make the program PDF again to publish it there.');

  const onChange = (e) => {
    const v = e.target.value;
    if (v.startsWith('entry-')) run(() => setSponsorsPosition(eventId, 'AFTER_ENTRY', Number(v.slice(6))), saved);
    else run(() => setSponsorsPosition(eventId, v, null), saved);
  };

  return (
    <label style={{ display: 'block', margin: '0 0 .7rem', fontSize: '.85rem', color: '#334155' }}>
      <span style={{ display: 'block', fontWeight: 600, marginBottom: '.25rem' }}>
        {t('programAdmin.pages.position', 'Where the sponsor pages stand (website and PDF)')}
      </span>
      <select value={value} disabled={busy} onChange={onChange} data-testid="sponsors-position"
        style={{ width: '100%', maxWidth: '36rem', padding: '.35rem .4rem', font: 'inherit' }}>
        <option value="BEFORE_PEOPLE">{t('programAdmin.pages.position.beforePeople', 'Below the program, above the speakers and faculty (default)')}</option>
        <option value="AFTER_PEOPLE">{t('programAdmin.pages.position.afterPeople', 'At the very end, below the speakers and faculty')}</option>
        {choices.length > 0 && (
          <optgroup label={t('programAdmin.pages.position.afterEntry', 'After this entry')}>
            {choices.map((c) => <option key={c.id} value={`entry-${c.id}`}>{c.text}</option>)}
          </optgroup>
        )}
        {position === 'AFTER_ENTRY' && !choices.some((c) => c.id === after) && (
          <option value={value} disabled>{t('programAdmin.pages.position.gone', 'After an entry that is no longer there')}</option>
        )}
      </select>
      <span style={{ display: 'block', marginTop: '.25rem', color: '#64748b' }}>
        {t('programAdmin.pages.position.hint', 'After an entry that the public does not see, they stand below the program instead. In the PDF every sponsor page is a page of its own.')}
      </span>
    </label>
  );
};

export const PdfPagesField = ({ eventId, part, view, onView, showTitle = true, program, language }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const t = useCallback((id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values), [intl]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const input = useRef(null);
  const pages = view?.[part] || [];
  const thumbs = useThumbnails(eventId, pages);
  const texts = PARTS[part];

  const run = async (action, done) => {
    setBusy(true); setMessage(null);
    try {
      onView(await action());
      if (done) setMessage({ text: done });
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      const text = (typeof err?.response?.data === 'string' && err.response.data)
        || t('programAdmin.pages.failed', 'Could not change the pages of the PDF.');
      setMessage({ error: true, text });
    } finally { setBusy(false); }
  };
  const saved = t('programAdmin.pages.saved', 'Saved. Make the program PDF again to publish it.');
  const shownSaved = t('programAdmin.pages.shownSaved', 'Saved. The website shows it once the program is published.');

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) run(() => addPdfPage(eventId, part, file), saved);
  };

  if (!view) return null;

  return (
    <div style={{ margin: '0 0 1rem', padding: '.8rem', border: '1px solid #e2e8f0', background: '#f8fafc' }} data-testid={`pdf-pages-${part}`}>
      {showTitle && <p style={{ margin: '0 0 .5rem', fontSize: '.9rem', fontWeight: 700, color: '#334155' }}>{t(...texts.title)}</p>}
      <p style={{ margin: '0 0 .7rem', fontSize: '.85rem', color: '#475569' }}>{t(...texts.intro)}</p>

      {message && (
        <p style={{ margin: '0 0 .7rem', padding: '.5rem .6rem', background: message.error ? '#fee2e2' : '#dcfce7', color: message.error ? '#991b1b' : '#166534', fontSize: '.85rem' }}>
          {message.text}
        </p>
      )}

      {part === 'sponsors' && (
        <SponsorsPlacement eventId={eventId} view={view} program={program} language={language} busy={busy} run={run} />
      )}

      {pages.length === 0 && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', margin: '0 0 .7rem' }}>
          {part === 'cover' && view.eventImageUsable && (
            <img src={mediaUrl(view.eventImageUrl)} alt={t('programAdmin.pages.eventImageAlt', 'Main image of the event page')}
              style={{ width: '5rem', border: '1px dashed #94a3b8', background: '#fff', opacity: 0.8 }} />
          )}
          <span style={{ fontSize: '.85rem', color: '#334155' }}>{t(...texts.none)}</span>
        </div>
      )}

      {pages.length > 0 && (
        <ol style={{ listStyle: 'none', margin: '0 0 .7rem', padding: 0, display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
          {pages.map((page, i) => (
            <li key={page.id} style={{ display: 'flex', gap: '.7rem', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', padding: '.4rem .5rem' }}>
              {page.kind === 'image' && thumbs[page.id]
                ? <img src={thumbs[page.id]} alt={t('programAdmin.pages.thumbAlt', 'Page {n}', { n: i + 1 })} style={{ width: '3rem', border: '1px solid #cbd5e1' }} />
                : <FileText size={28} aria-hidden="true" style={{ color: '#64748b', width: '3rem' }} />}
              <span style={{ flex: 1, minWidth: 0, fontSize: '.85rem', color: '#334155', overflowWrap: 'anywhere' }}>
                {page.fileName}
                {page.kind === 'pdf' && (
                  <span style={{ color: '#64748b' }}> · {t('programAdmin.pages.pdfPages', '{count, plural, one {# page} other {# pages}}', { count: page.pageCount })}</span>
                )}
                {page.website === false && page.pdf === false && (
                  <span style={{ display: 'block', color: '#b45309' }}>{t('programAdmin.pages.shownNowhere', 'Shown nowhere at the moment')}</span>
                )}
              </span>
              {['website', 'pdf'].map((where) => (
                <label key={where} style={{ display: 'inline-flex', alignItems: 'center', gap: '.25rem', fontSize: '.8rem', color: '#334155', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={page[where] !== false}
                    disabled={busy}
                    aria-label={t(`programAdmin.pages.shown.${where}`, where === 'website' ? 'Show {name} on the website' : 'Show {name} in the PDF', { name: page.fileName })}
                    onChange={(e) => run(() => setPdfPageShown(eventId, page.id, { [where]: e.target.checked }), where === 'website' ? shownSaved : saved)}
                  />
                  {where === 'website' ? t('programAdmin.pages.website', 'Website') : 'PDF'}
                </label>
              ))}
              <Button type="button" secondary disabled={busy || i === 0} aria-label={t('programAdmin.pages.up', 'Move {name} up', { name: page.fileName })}
                onClick={() => run(() => movePdfPage(eventId, page.id, true))}>
                <ArrowUp size={14} aria-hidden="true" />
              </Button>
              <Button type="button" secondary disabled={busy || i === pages.length - 1} aria-label={t('programAdmin.pages.down', 'Move {name} down', { name: page.fileName })}
                onClick={() => run(() => movePdfPage(eventId, page.id, false))}>
                <ArrowDown size={14} aria-hidden="true" />
              </Button>
              <Button type="button" danger disabled={busy} aria-label={t('programAdmin.pages.remove', 'Remove {name}', { name: page.fileName })}
                onClick={() => run(() => removePdfPage(eventId, page.id), saved)}>
                <Trash2 size={14} aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ol>
      )}

      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
        <input ref={input} type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
          style={{ display: 'none' }} onChange={onFile} data-testid={`pdf-pages-${part}-input`} />
        <Button type="button" disabled={busy} onClick={() => input.current?.click()}>
          <Upload size={15} aria-hidden="true" />
          <FormattedMessage id="programAdmin.pages.add" defaultMessage="Add a file" />
        </Button>
        {part === 'cover' && pages.length === 0 && view.eventImageUsable && (
          <Button type="button" secondary disabled={busy} onClick={() => run(() => addEventImagePage(eventId), saved)}>
            <ImagePlus size={15} aria-hidden="true" />
            <FormattedMessage id="programAdmin.pages.useEventImage" defaultMessage="Use the event page's image" />
          </Button>
        )}
      </div>
      <PanelHint>
        <FormattedMessage id="programAdmin.pages.hint"
          defaultMessage="Best in A4 portrait (e.g. 1414 x 2000 pixels): then an image fills the page to the edges. The pages are in every PDF made here from the program (the program PDF, its preview and “Export as PDF”): an uploaded program PDF, e.g. from Canva, is published as it is, without them." />
      </PanelHint>
    </div>
  );
};

// The two entries of the Program area: title pages and sponsor pages, each with how many files it has,
// opening its field below.
export const PdfPagesEntries = ({ eventId, program, language }) => {
  const intl = useIntl();
  const [view, setView] = usePdfPages(eventId);
  const [open, setOpen] = useState(null);
  if (!view) return null;
  return (
    <div style={{ margin: '0 0 .8rem', border: '1px solid #dbe3ea', background: '#fff' }} data-testid="pdf-pages-entries">
      {['cover', 'sponsors'].map((part) => {
        const count = view[part]?.length || 0;
        const isOpen = open === part;
        const Icon = isOpen ? ChevronDown : ChevronRight;
        return (
          <div key={part} style={{ borderTop: part === 'sponsors' ? '1px solid #e2e8f0' : 'none' }}>
            <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : part)}
              style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '.5rem', padding: '.55rem .7rem', border: 0, background: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left', color: '#0f172a' }}>
              <Icon size={15} aria-hidden="true" />
              <span style={{ fontWeight: 700, fontSize: '.9rem' }}>{intl.formatMessage({ id: PARTS[part].title[0], defaultMessage: PARTS[part].title[1] })}</span>
              <span style={{ marginLeft: 'auto', fontSize: '.8rem', color: '#64748b' }}>
                {intl.formatMessage({ id: 'programAdmin.pages.count', defaultMessage: '{count, plural, =0 {none} one {# file} other {# files}}' }, { count })}
              </span>
            </button>
            {isOpen && (
              <div style={{ padding: '0 .7rem .2rem' }}>
                <PdfPagesField eventId={eventId} part={part} view={view} onView={setView} showTitle={false} program={program} language={language} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// The title pages alone, for the Publish area next to "Make the program PDF".
export const PdfCoverPages = ({ eventId }) => {
  const [view, setView] = usePdfPages(eventId);
  return <PdfPagesField eventId={eventId} part="cover" view={view} onView={setView} />;
};
